import React, { useRef, useState } from 'react'
import { Avatar, Button, Dropdown, Input, Menu, Spin, Tag } from 'antd'
import moment from 'moment'
import { FileRecord, LeagueUpdateCommentType, LeagueUpdateRespType, LeagueUpdateType, ReactionInfo } from 'types/league/leagueUpdates'
import ViewAddress from 'helper/Map/ViewAddress'
import NewsImageContainer from 'pages/Newsfeed/NewsImageContainer'
import Actionicon from 'assets/Images/Action.svg'
import DocumentIcon from 'assets/Images/document.svg'
import MusicIcon from 'assets/Images/musicIcon.svg'
import PlayerIcon from 'assets/Images/playerIcon.svg'
import like from 'assets/Images/like.svg'
import love from 'assets/Images/love.svg'
import information from 'assets/Images/information.svg'
import likeemojis from 'assets/Images/likeemojis.svg'
import {
  useAddLeagueUpdateComment,
  useAddReaction,
  useDeleteLeagueUpdate,
  useDeleteLeagueUpdateComment,
  useGetLeagueUpdateComments,
  usePinLeagueUpdate,
  useUnpinLeagueUpdate,
} from 'api/league/leagueUpdates/leagueUpdates'
import { leagueUpdatesStore } from 'state/league/leagueUpdates'
import 'pages/Newsfeed/Style/newsStyle.scss'

interface UpdateCardProps {
  update: LeagueUpdateRespType
}

type ReactionType = 'like' | 'love' | 'informative'

const TYPE_LABELS: Record<LeagueUpdateType, string> = {
  liveUpdate: 'Live Update',
  news: 'Live Update',
  announcement: 'Announcement',
  reminder: 'Reminder',
  result: 'Result',
  other: 'Other',
}

let activeAudioElement: HTMLAudioElement | null = null
let activeVideoElement: HTMLVideoElement | null = null

const UpdateCard: React.FC<UpdateCardProps> = ({ update }) => {
  const [activeReaction, setActiveReaction] = useState<ReactionType | null>(
    update.reactionInfo?.selfReactionType ?? null
  )
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [commentText, setCommentText] = useState('')
  const currentAudioRef = useRef<HTMLAudioElement | null>(null)
  const currentVideoRef = useRef<HTMLVideoElement | null>(null)

  const { setSelectedUpdate, setEditUpdateModal } = leagueUpdatesStore(store => ({
    setSelectedUpdate: store.setSelectedUpdate,
    setEditUpdateModal: store.setEditUpdateModal,
  }))

  const { mutate: pinUpdate, isLoading: pinning }         = usePinLeagueUpdate()
  const { mutate: unpinUpdate, isLoading: unpinning }     = useUnpinLeagueUpdate()
  const { mutate: deleteUpdate }                          = useDeleteLeagueUpdate()
  const { mutate: addReaction }                           = useAddReaction()
  const { mutate: addComment, isLoading: addingComment }  = useAddLeagueUpdateComment()
  const { mutate: deleteComment }                         = useDeleteLeagueUpdateComment()

  const { data: commentsData, isLoading: commentsLoading } = useGetLeagueUpdateComments(
    update._id,
    1,
    commentsOpen,
  )

  const handlePinToggle = () => {
    if (update.isPinned) unpinUpdate(update._id)
    else pinUpdate(update._id)
  }

  const handleEdit = () => {
    setSelectedUpdate(update)
    setEditUpdateModal(true)
  }

  const handleDelete = () => { deleteUpdate(update._id) }

  const handleReaction = (type: ReactionType) => {
    const next = activeReaction === type ? 'none' : type
    setActiveReaction(next === 'none' ? null : type)
    addReaction({
      updateId: update._id,
      payload: { userId: localStorage.getItem('adminId') || '', reaction: next as any },
    })
  }

  const handleAddComment = () => {
    if (!commentText.trim()) return
    addComment(
      { updateId: update._id, payload: { content: commentText } },
      { onSuccess: () => setCommentText('') },
    )
  }

  const handleAudioPlay = (audioEl: HTMLAudioElement) => {
    if (activeAudioElement && activeAudioElement !== audioEl) {
      activeAudioElement.pause()
      activeAudioElement.currentTime = 0
    }
    if (activeVideoElement) {
      activeVideoElement.pause()
      activeVideoElement.currentTime = 0
      activeVideoElement = null
      currentVideoRef.current = null
    }
    activeAudioElement = audioEl
    currentAudioRef.current = audioEl
  }

  const handleAudioEnd = (audioEl: HTMLAudioElement) => {
    if (currentAudioRef.current === audioEl) currentAudioRef.current = null
    if (activeAudioElement === audioEl) activeAudioElement = null
  }

  const handleVideoPlay = (videoEl: HTMLVideoElement) => {
    if (activeVideoElement && activeVideoElement !== videoEl) {
      activeVideoElement.pause()
      activeVideoElement.currentTime = 0
    }
    if (activeAudioElement) {
      activeAudioElement.pause()
      activeAudioElement.currentTime = 0
      activeAudioElement = null
      currentAudioRef.current = null
    }
    activeVideoElement = videoEl
    currentVideoRef.current = videoEl
  }

  const moreMenu = (
    <Menu>
      <Menu.Item key="edit" onClick={handleEdit}>Edit post</Menu.Item>
      <Menu.Item key="pin" onClick={handlePinToggle} disabled={pinning || unpinning}>
        {update.isPinned ? 'Unpin post' : 'Pin post'}
      </Menu.Item>
      <Menu.Item key="delete" onClick={handleDelete} danger>Delete post</Menu.Item>
    </Menu>
  )

  const author = update.admin
    ? { name: `${update.admin.firstName} ${update.admin.lastName}`, photo: update.admin.photo }
    : update.organizer
    ? { name: update.organizer.name, photo: update.organizer.profileImage }
    : { name: 'Bamrec', photo: undefined }

  const authorInitial = author.name.trim().charAt(0).toUpperCase()
  const authorPhotoUrl = author.photo && typeof author.photo === 'string' ? author.photo : undefined

  const commentCount = update.commentCount ?? 0
  const ri: ReactionInfo = update.reactionInfo ?? { reactionsCount: 0, likeReactionsCount: 0, loveReactionsCount: 0, informativeReactionsCount: 0 }

  // Normalise image records so NewsImageContainer (which reads .post) works
  const imageRecords = (update.images || []).map((f: FileRecord) => ({
    ...f,
    post: f.post || f.thumb || f.original,
  }))

  return (
    <div className="br-12 basic_newsContainer" style={{ marginTop: 0 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="px-20 py-20 d-flex justify-between align-items-center">
        <div className="d-flex gap-20 align-items-center">
          <Avatar
            size={50}
            src={authorPhotoUrl}
            style={!authorPhotoUrl ? { backgroundColor: '#EBEDF1', color: '#E69741', fontWeight: 600, fontSize: 18 } : undefined}
          >
            {authorInitial}
          </Avatar>
          <div>
            <div className="d-flex gap-10 align-items-center">
              <h3 className="font14 b1">{author.name}</h3>
              <p className="gray_text">{moment(update.createdAt).fromNow()}</p>
              {update.isPinned && (
                <Tag
                  style={{
                    border: 'none',
                    backgroundColor: '#E8F6FB',
                    color: '#2ba7ca',
                    borderRadius: 15,
                    padding: '2px 10px',
                    fontWeight: 'bold',
                  }}
                >
                  Pinned
                </Tag>
              )}
              {update.type && (
                <Tag
                  style={{
                    border: 'none',
                    backgroundColor: '#FFF6EC',
                    color: '#E69741',
                    borderRadius: 15,
                    padding: '2px 10px',
                    fontWeight: 'bold',
                  }}
                >
                  {TYPE_LABELS[update.type] ?? update.type}
                </Tag>
              )}
            </div>
          </div>
        </div>
        <Dropdown overlay={moreMenu} trigger={['click']} placement="bottomRight">
          <img alt="more" src={Actionicon} style={{ cursor: 'pointer' }} />
        </Dropdown>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="px-20 mt-10 br-12 news-feed">
        {update.title && (
          <div className="mt-16">
            <h3 className="font14 b1">{update.title}</h3>
          </div>
        )}

        <p className="mt-10">{update.content}</p>

        {/* Images */}
        {imageRecords.length > 0 && (
          <NewsImageContainer images={imageRecords} />
        )}

        {/* Videos */}
        {(update.videos || []).map((f: FileRecord) => (
          <div key={f._id} className="mt-16" style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #EBEDF1', background: '#000' }}>
            <video
              src={f.link || f.original}
              poster={f.post || f.thumb || undefined}
              style={{ width: '100%', maxHeight: 320, display: 'block', objectFit: 'contain' }}
              controls
              onPlay={e => handleVideoPlay(e.currentTarget)}
              onEnded={e => handleVideoPlay(e.currentTarget)}
            />
            {f.name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#FAFAFA', borderTop: '1px solid #EBEDF1' }}>
                <img src={PlayerIcon} style={{ width: 16, height: 16, flexShrink: 0 }} alt="video" />
                <span style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.name}{f.ext ? (f.ext.startsWith('.') ? f.ext : `.${f.ext}`) : ''}
                </span>
              </div>
            )}
          </div>
        ))}

        {/* Audio */}
        {(update.audios || []).map((f: FileRecord) => (
          <div key={f._id} className="mt-16" style={{ border: '1px solid #EBEDF1', borderRadius: 12, overflow: 'hidden', background: '#FAFAFA' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid #EBEDF1' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#FFF6EC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img src={MusicIcon} style={{ width: 18, height: 18 }} alt="audio" />
              </div>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name ? `${f.name}${f.ext ? (f.ext.startsWith('.') ? f.ext : `.${f.ext}`) : ''}` : 'Audio'}
              </span>
            </div>
            <div style={{ padding: '10px 14px' }}>
              <audio
                src={f.link || f.original}
                controls
                style={{ width: '100%', height: 40 }}
                onPlay={e => handleAudioPlay(e.currentTarget)}
                onEnded={e => handleAudioEnd(e.currentTarget)}
              />
            </div>
          </div>
        ))}

        {/* Documents */}
        {(update.documents || []).map((f: FileRecord) => {
          const isPdf = f.ext?.toLowerCase().replace('.', '') === 'pdf'
          const fileName = f.name ? `${f.name}${f.ext ? (f.ext.startsWith('.') ? f.ext : `.${f.ext}`) : ''}` : 'Document'
          const fileUrl = f.original || ''
          return (
            <div key={f._id} className="mt-16">
              {isPdf && fileUrl ? (
                <div style={{ border: '1px solid #EBEDF1', borderRadius: 12, overflow: 'hidden' }}>
                  <object
                    data={fileUrl}
                    type="application/pdf"
                    width="100%"
                    height="400px"
                    style={{ display: 'block' }}
                  >
                    <div style={{ padding: '16px', textAlign: 'center', color: '#908F98', fontSize: 13 }}>
                      PDF preview not supported in this browser.
                    </div>
                  </object>
                  <div style={{ borderTop: '1px solid #EBEDF1', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, background: '#FAFAFA' }}>
                    <img src={DocumentIcon} style={{ width: 18, height: 18, flexShrink: 0 }} alt="pdf" />
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                    <a href={fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#2ba7ca', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      Open PDF
                    </a>
                  </div>
                </div>
              ) : (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="update-document-item"
                >
                  <img src={DocumentIcon} style={{ width: 18, height: 18, flexShrink: 0 }} alt="doc" />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                  <span style={{ fontSize: 12, color: '#2ba7ca', fontWeight: 500 }}>Download</span>
                </a>
              )}
            </div>
          )
        })}

        {/* Location */}
        {update.address && (
          <div className="mt-10 br-12">
            <div className="mt-10 mb-16 gap-16 d-flex align-items-center justify-between">
              <h3 className="font14 b-2">
                {[update.address.street, update.address.city, update.address.country]
                  .filter(Boolean)
                  .join(', ')}
              </h3>
              <a
                href={`https://www.google.com/maps/search/${encodeURIComponent(
                  [update.address.street, update.address.city, update.address.country]
                    .filter(Boolean)
                    .join(', '),
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="stepper-button">View location</Button>
              </a>
            </div>
            <ViewAddress onlyView={true} mapData={update.address} />
          </div>
        )}
      </div>

      {/* ── Reactions row ───────────────────────────────────────────────────── */}
      <div className="px-20 like_count d-flex align-items-center" style={{ marginTop: 8, gap: 8 }}>
        <ReactionIcons activeReaction={activeReaction} />
        {ri.reactionsCount > 0 && (
          <span style={{ fontSize: 13, color: '#908F98' }}>{ri.reactionsCount}</span>
        )}
      </div>

      {/* ── Action buttons ──────────────────────────────────────────────────── */}
      <div className="action-row" style={{ borderTop: '1px solid #efeff2', display: 'flex', padding: '8px 24px' }}>
        <button
          className="action-btn"
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8, fontSize: 12, fontWeight: 500, color: activeReaction === 'like' ? '#2ba7ca' : '#1a1a1a' }}
          onClick={() => handleReaction('like')}
        >
          <img src={like} width={22} height={22} alt="like" /> Like{ri.likeReactionsCount > 0 ? ` · ${ri.likeReactionsCount}` : ''}
        </button>
        <button
          className="action-btn"
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8, fontSize: 12, fontWeight: 500, color: activeReaction === 'love' ? '#e0395b' : '#1a1a1a' }}
          onClick={() => handleReaction('love')}
        >
          <img src={love} width={22} height={22} alt="love" /> Love{ri.loveReactionsCount > 0 ? ` · ${ri.loveReactionsCount}` : ''}
        </button>
        <button
          className="action-btn"
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8, fontSize: 12, fontWeight: 500, color: activeReaction === 'informative' ? '#f5a623' : '#1a1a1a' }}
          onClick={() => handleReaction('informative')}
        >
          <img src={information} width={22} height={22} alt="informative" /> Informative{ri.informativeReactionsCount > 0 ? ` · ${ri.informativeReactionsCount}` : ''}
        </button>
        <button
          className="action-btn"
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8, fontSize: 12, fontWeight: 500, color: commentsOpen ? '#E69741' : '#1a1a1a' }}
          onClick={() => setCommentsOpen(o => !o)}
        >
          💬 {commentCount > 0 ? `${commentCount} Comment${commentCount > 1 ? 's' : ''}` : 'Comment'}
        </button>
      </div>

      {/* ── Comments ────────────────────────────────────────────────────────── */}
      {commentsOpen && (
        <div className="update-comments-section">
          {commentsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <Spin size="small" />
            </div>
          ) : (
            <>
              {(commentsData?.records || []).length === 0 && (
                <p className="comments-empty">No comments yet. Be the first to comment.</p>
              )}
              {(commentsData?.records || []).map((comment: LeagueUpdateCommentType) => {
                const commentAuthor = comment.admin
                  ? `${comment.admin.firstName} ${comment.admin.lastName}`
                  : comment.organizer
                  ? `${comment.organizer.firstName} ${comment.organizer.lastName}`
                  : 'Unknown'
                const initial = commentAuthor.charAt(0).toUpperCase()

                return (
                  <div key={comment._id} className="update-comment-item">
                    <div className="comment-avatar">{initial}</div>
                    <div className="comment-body">
                      <div className="comment-header">
                        <span className="comment-author">{commentAuthor}</span>
                        <span className="comment-time">{moment(comment.createdAt).fromNow()}</span>
                      </div>
                      <p className="comment-content">{comment.content}</p>
                    </div>
                    <button
                      className="comment-delete-btn"
                      onClick={() => deleteComment(comment._id)}
                      title="Delete comment"
                    >
                      ✕
                    </button>
                  </div>
                )
              })}
            </>
          )}

          <div className="comment-input-row">
            <Input
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Write a comment..."
              onPressEnter={handleAddComment}
              style={{ borderRadius: 8 }}
            />
            <button
              className="comment-post-btn"
              onClick={handleAddComment}
              disabled={!commentText.trim() || addingComment}
            >
              Post
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Shows the reaction emoji icons based on which reactions are active
const ReactionIcons: React.FC<{ activeReaction: ReactionType | null }> = ({ activeReaction }) => {
  if (!activeReaction) return <img src={like} width={40} height={40} alt="like" />
  if (activeReaction === 'like') return <img src={like} width={40} height={40} alt="like" />
  if (activeReaction === 'love') return <img src={love} width={40} height={40} alt="love" />
  return <img src={information} width={40} height={40} alt="informative" />
}

export default UpdateCard
